import { useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { ListNode, ListItemNode } from '@lexical/list';
import { EditorEventPlugin } from '../Plugins/EditorEventPlugin';
import QuickRepliesContainer from '../../../Common/QuickRepliesContainer';
import ChatQuickReplies from '../ChatQuickReplies';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import LinkButton from '../LinkButton';
import { MATCHERS, validateUrl } from '../../../../../../utils/regex';
import ItalicIcon from '../../../../../../assets/italic.svg';
import BoldIcon from '../../../../../../assets/bold.svg';
import BulletListIcon from '../../../../../../assets/bulletList.svg';
import NumberedListIcon from '../../../../../../assets/numberedList.svg';
import MarkdownButton from '../MarkdownButton';
import MarkdownListButton from '../MarkdownListButton';
import SendButton from '../SendButton';
import EmojiContainer from '../EmojiContainer';
import { useChatContext } from '../../../../../../contexts/ChatContext';
import { MarkdownPastePlugin } from '../Plugins/MarkdownPastePlugin';
import { Box } from '@coolblue-development/freddy';

const theme = {
  text: {
    bold: 'bold',
    italic: 'italic'
  }
};

const initialConfig = {
  namespace: 'MyEditor',
  theme,
  nodes: [ListNode, ListItemNode, LinkNode, AutoLinkNode],
  onError: () => {
    console.error();
  }
};

const Editor = (): JSX.Element => {
  const { selectedChat } = useChatContext();
  const [activeListType, setActiveListType] = useState<'ordered' | 'unordered' | null>(null);
  const [format, setFormat] = useState<[boolean, boolean]>([false, false]);
  const [isBold, isItalic] = format;

  return (
    <Box sx={{ position: 'relative' }}>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={
            <Box sx={{ margin: '14px 0' }}>
              <ContentEditable className="contentEditable" />
            </Box>
          }
          placeholder={<Box className="placeholder">Type a message...</Box>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <MarkdownPastePlugin />
        <EditorEventPlugin setFormat={setFormat} onChange={() => {}} setActiveListType={setActiveListType} />
        <AutoFocusPlugin />
        <HistoryPlugin />
        <ListPlugin />
        <AutoLinkPlugin matchers={MATCHERS} />
        <ClickableLinkPlugin />
        <LinkPlugin validateUrl={validateUrl} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <QuickRepliesContainer
              channel="chat"
              selectedContact={selectedChat}
              QuickRepliesComponent={ChatQuickReplies}
            />
            <EmojiContainer />
            <MarkdownButton formatType="bold" isActive={isBold} Icon={BoldIcon} />
            <MarkdownButton formatType="italic" isActive={isItalic} Icon={ItalicIcon} />
            <MarkdownListButton
              listType="unordered"
              activeListType={activeListType}
              setActiveListType={setActiveListType}
              Icon={BulletListIcon}
            />
            <MarkdownListButton
              listType="ordered"
              activeListType={activeListType}
              setActiveListType={setActiveListType}
              Icon={NumberedListIcon}
            />
            <LinkButton />
          </Box>
          <SendButton />
        </Box>
      </LexicalComposer>
    </Box>
  );
};

export default Editor;
